import { FC } from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
} from "@mui/material";

import { CountryMultiSelect } from "../Countries/CountryMultiSelect";
import { CountryMultiSelectSortable } from "../Countries/CountryMultiSelectSortable";
import { InnovatricsDocTypesMultiSelect } from "./InnovatricsDocTypesMultiSelect";
import { FineTuneLine, FineTuneLineData } from "./FineTuneLine";

type InnovatricsSettingsProps = {
  showOnlyCountries: string[];
  excludeCountries: string[];
  showFirstCountries: string[];
  showOnlyDocTypes: string[];
  excludeDocTypes: string[];
  documentRetries: string;
  fineTuneLines: FineTuneLineData[];
  onShowOnlyCountriesChange: (value: string[]) => void;
  onExcludeCountriesChange: (value: string[]) => void;
  onShowFirstCountriesChange: (value: string[]) => void;
  onShowOnlyDocTypesChange: (value: string[]) => void;
  onExcludeDocTypesChange: (value: string[]) => void;
  onDocumentRetriesChange: (value: string) => void;
  onFineTuneLinesChange: (lines: FineTuneLineData[]) => void;
  onAddFineTuneLine: () => void;
  onUpdateFineTuneLine: (line: FineTuneLineData) => void;
  onRemoveFineTuneLine: (id: string) => void;
};

export const InnovatricsSettings: FC<InnovatricsSettingsProps> = ({
  showOnlyCountries,
  excludeCountries,
  showFirstCountries,
  showOnlyDocTypes,
  excludeDocTypes,
  documentRetries,
  fineTuneLines,
  onShowOnlyCountriesChange,
  onExcludeCountriesChange,
  onShowFirstCountriesChange,
  onShowOnlyDocTypesChange,
  onExcludeDocTypesChange,
  onDocumentRetriesChange,
  onAddFineTuneLine,
  onUpdateFineTuneLine,
  onRemoveFineTuneLine,
}) => {
  return (
    <Stack spacing={2}>
      {/* List of Countries Section */}
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 700, color: "#1C1F21", mt: 1 }}
      >
        List of Countries
      </Typography>

      <CountryMultiSelect
        label="Show only"
        name="innovatrics_show_only"
        value={showOnlyCountries}
        onChange={onShowOnlyCountriesChange}
      />

      <CountryMultiSelect
        label="Exclude"
        name="innovatrics_exclude"
        value={excludeCountries}
        onChange={onExcludeCountriesChange}
      />

      <CountryMultiSelectSortable
        label="Show first"
        name="innovatrics_show_first"
        value={showFirstCountries}
        onChange={onShowFirstCountriesChange}
      />

      {/* Document Types Section */}
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 700, color: "#1C1F21", mt: 3 }}
      >
        Document types
      </Typography>

      <InnovatricsDocTypesMultiSelect
        label="Show only"
        name="innovatrics_doc_types_show_only"
        value={showOnlyDocTypes}
        onChange={onShowOnlyDocTypesChange}
      />

      <InnovatricsDocTypesMultiSelect
        label="Exclude"
        name="innovatrics_doc_types_exclude"
        value={excludeDocTypes}
        onChange={onExcludeDocTypesChange}
      />

      {/* Fine-tuning lines */}
      {fineTuneLines.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "#1C1F21", mb: 1 }}
          >
            Fine tuning rules:
          </Typography>
          <Stack spacing={1}>
            {fineTuneLines.map((line) => (
              <FineTuneLine
                key={line.id}
                data={line}
                onChange={onUpdateFineTuneLine}
                onRemove={onRemoveFineTuneLine}
              />
            ))}
          </Stack>
        </Box>
      )}

      <Box sx={{ mt: 1, ml: 2 }}>
        <Typography
          component="a"
          href="#"
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            onAddFineTuneLine();
          }}
          sx={{
            color: "#1976d2",
            textDecoration: "none",
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          + fine tune
        </Typography>
      </Box>

      {/* Other Section */}
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 700, color: "#1C1F21", mt: 3 }}
      >
        Other
      </Typography>

      <TextField
        label="Retries to scan document"
        type="number"
        variant="filled"
        inputProps={{ min: 0 }}
        value={documentRetries}
        onChange={(e) => onDocumentRetriesChange(e.target.value)}
        helperText="After this number of retries we move on to liveness check"
      />
    </Stack>
  );
};

export default InnovatricsSettings;
